import qs from 'qs';

import { json_headers, request } from './common';

export type ExtranetPendingChangeStatus = 'pending' | 'approved' | 'rejected' | 'superseded';
export type ExtranetPendingChangeRequestType =
  | 'property'
  | 'property_meta'
  | 'property_taxes'
  | 'property_facility'
  | 'property_image'
  | 'room_type'
  | 'room_availability'
  | 'rate_plan'
  | 'promotional_discount';

export interface ExtranetPendingChange {
  id: string;
  fk_vendor_id: string;
  request_type: ExtranetPendingChangeRequestType;
  target_ids: Record<string, string>;
  previous_value: Record<string, unknown> | null;
  proposed_value: Record<string, unknown>;
  status: ExtranetPendingChangeStatus;
  requested_by_user_id: string;
  reviewed_by_user_id: string | null;
  reviewed_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface ListExtranetPendingChangesParams {
  status?: string;
  vendor_id?: string;
  request_type?: ExtranetPendingChangeRequestType;
  submitted_by_user_id?: string;
  property_id?: string;
  created_from?: string;
  created_to?: string;
  page?: number;
  limit?: number;
}

export interface ListExtranetPendingChangesResponse {
  status: number;
  message: string;
  result: ExtranetPendingChange[];
  total: number;
}

export interface BulkActionResult {
  id: string;
  status: 'approved' | 'rejected' | 'skipped';
  reason?: string;
}

export interface BulkActionResponse {
  status: number;
  message: string;
  results: BulkActionResult[];
  approved?: number;
  rejected?: number;
  skipped: number;
}

export default class ExtranetService {
  static basePath() {
    return window.configs.API_HOST + '/api/vendor/admin/extranet-pending-changes';
  }

  static listPendingChanges(
    params: ListExtranetPendingChangesParams = {},
    options?: { signal?: AbortSignal },
  ): Promise<ListExtranetPendingChangesResponse> {
    const query = qs.stringify(params, { skipNulls: true });
    return request(`${this.basePath()}${query ? `?${query}` : ''}`, { signal: options?.signal });
  }

  static getPendingChangeById(
    id: string,
    options?: { signal?: AbortSignal },
  ): Promise<{ status: number; result: ExtranetPendingChange }> {
    return request(`${this.basePath()}/${id}`, { signal: options?.signal });
  }

  static approveChange(id: string): Promise<{ status: number; message: string; result: ExtranetPendingChange }> {
    return request(`${this.basePath()}/${id}/approve`, {
      method: 'POST',
      headers: json_headers,
      body: JSON.stringify({}),
    });
  }

  static rejectChange(id: string): Promise<{ status: number; message: string; result: ExtranetPendingChange }> {
    return request(`${this.basePath()}/${id}/reject`, {
      method: 'POST',
      headers: json_headers,
      body: JSON.stringify({}),
    });
  }

  static bulkApprove(ids: string[]): Promise<BulkActionResponse> {
    return request(`${this.basePath()}/bulk-approve`, {
      method: 'POST',
      headers: json_headers,
      body: JSON.stringify({ ids }),
    });
  }

  static bulkReject(ids: string[]): Promise<BulkActionResponse> {
    return request(`${this.basePath()}/bulk-reject`, {
      method: 'POST',
      headers: json_headers,
      body: JSON.stringify({ ids }),
    });
  }
}
